<template>
    <div class="word-page">
        <!-- 视图切换：导航卡片 / 单词列表 -->
        <transition name="view-fade" mode="out-in">
            <!-- 导航卡片（年级分册 / 单元列表视图共用） -->
            <el-card v-if="viewLevel !== 'word'" key="nav-card" shadow="always" class="filter-card">
                <div slot="header" class="card-header">
                    <div class="card-header-left">
                        <i class="el-icon-document-copy"></i>
                        <span>单词管理</span>
                        <el-divider direction="vertical"></el-divider>
                        <span v-if="viewLevel === 'gradeVolume'" class="header-sub">选择年级分册</span>
                        <template v-else>
                            <el-button type="text" icon="el-icon-back" @click="goToGradeVolume" class="back-btn">返回年级分册</el-button>
                            <el-divider direction="vertical"></el-divider>
                            <span class="header-sub">{{ currentGradeVolumeText }}</span>
                        </template>
                    </div>
                </div>
                <transition name="view-fade" mode="out-in">
                    <!-- 第一层：年级分册列表 -->
                    <div v-if="viewLevel === 'gradeVolume'" key="grade">
                        <div v-for="grade in gradeVolumeStats" :key="grade.gradeId" class="grade-group">
                            <div class="grade-title">{{ grade.gradeName }}</div>
                            <el-row :gutter="16">
                                <el-col :span="6" v-for="volume in grade.volumes" :key="volume.volume">
                                    <el-card shadow="hover" class="nav-card grade-volume-card" @click.native="selectGradeVolume(grade.gradeId, grade.gradeName, volume.volume)">
                                        <div class="card-title">{{ volume.volume }}</div>
                                        <div class="card-info">{{ volume.unitCount }} 个单元 / {{ volume.wordCount || 0 }} 个单词</div>
                                        <div class="card-arrow"><i class="el-icon-arrow-right"></i></div>
                                        <div class="card-batch-import" @click.stop="openBatchImportDialog(grade.gradeId, grade.gradeName, volume.volume)">
                                            <i class="el-icon-upload2"></i>
                                            <span>批量导入</span>
                                        </div>
                                    </el-card>
                                </el-col>
                            </el-row>
                        </div>
                        <el-empty v-if="gradeVolumeStats.length === 0" description="暂无数据"></el-empty>
                    </div>

                    <!-- 第二层：单元列表 -->
                    <div v-else-if="viewLevel === 'unit'" key="unit">
                        <div v-if="navLoading" class="inline-loading">
                            <i class="el-icon-loading"></i>
                            <span>加载中...</span>
                        </div>
                        <template v-else>
                            <el-row :gutter="16">
                                <el-col :span="6" v-for="unit in filteredUnitsByGradeVolume" :key="unit.id">
                                    <el-card shadow="hover" class="nav-card unit-card" @click.native="selectUnit(unit)">
                                        <div class="card-title">{{ unit.unitName }}</div>
                                        <div class="card-info">第{{ unit.unitNumber }}单元 / {{ unit.wordCount || 0 }} 个单词</div>
                                        <div class="card-arrow"><i class="el-icon-arrow-right"></i></div>
                                    </el-card>
                                </el-col>
                            </el-row>
                            <el-empty v-if="filteredUnitsByGradeVolume.length === 0" description="该分册暂无单元"></el-empty>
                        </template>
                    </div>
                </transition>
            </el-card>

            <!-- 第三层：单词列表 -->
            <div v-else key="word-view">
                <!-- 查询筛选卡片 -->
                <el-card shadow="always" class="filter-card">
                    <div slot="header" class="card-header">
                        <div class="card-header-left">
                            <i class="el-icon-document-copy"></i>
                            <span>单词管理</span>
                            <el-divider direction="vertical"></el-divider>
                            <el-button type="text" icon="el-icon-back" @click="goToUnitList" class="back-btn">返回单元列表</el-button>
                            <el-divider direction="vertical"></el-divider>
                            <span class="header-sub">{{ currentUnitLabel }}</span>
                        </div>
                    </div>
                    <el-form :inline="true" :model="query" class="filter-form">
                        <el-form-item label="单词">
                            <el-input clearable v-model="query.word" placeholder="请输入单词" style="width: 160px"></el-input>
                        </el-form-item>
                        <el-form-item>
                            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
                            <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
                        </el-form-item>
                    </el-form>
                </el-card>

                <!-- 表格卡片 -->
                <el-card shadow="always" class="table-card">
                    <div slot="header" class="card-header">
                        <div class="card-header-left">
                            <span>数据列表</span>
                            <span class="total-badge">共 <b>{{ total }}</b> 条</span>
                        </div>
                        <div class="card-header-right">
                            <el-button type="warning" size="small" icon="el-icon-upload2" @click="showImportDialog">导入</el-button>
                        </div>
                    </div>
                    <el-table
                        :data="list"
                        border
                        stripe
                        v-loading="tableLoading"
                        element-loading-text="加载中..."
                        style="width: 100%">
                        <el-table-column type="index" label="序号" width="70" align="center"/>
                        <el-table-column prop="word" label="单词" width="120" show-overflow-tooltip/>
                        <el-table-column prop="chineseMeaning" label="中文释义" show-overflow-tooltip/>
                        <el-table-column prop="partOfSpeech" label="词性" width="80" align="center"/>
                        <el-table-column prop="phoneticUk" label="英音" width="100" show-overflow-tooltip/>
                        <el-table-column prop="phoneticUs" label="美音" width="100" show-overflow-tooltip/>
                        <el-table-column label="固定搭配" min-width="150" show-overflow-tooltip>
                            <template slot-scope="s">{{ formatJsonArr(s.row.collocations) }}</template>
                        </el-table-column>
                        <el-table-column label="派生词" min-width="150" show-overflow-tooltip>
                            <template slot-scope="s">{{ formatJsonArr(s.row.wordForms) }}</template>
                        </el-table-column>
                        <el-table-column align="center" label="操作" width="180" fixed="right">
                            <template slot-scope="scope">
                                <div class="action-btns">
                                    <el-button type="primary" size="mini" icon="el-icon-edit" @click="btnInsert(scope.row)">编辑</el-button>
                                    <el-button type="danger" size="mini" icon="el-icon-delete" @click="btnDelete(scope.row.id)">删除</el-button>
                                </div>
                            </template>
                        </el-table-column>
                    </el-table>

                    <el-pagination
                        style="margin-top: 16px;"
                        background
                        layout="total, sizes, prev, pager, next, jumper"
                        :page-sizes="[10, 20, 50, 100]"
                        @size-change="sizeChange"
                        @current-change="currentChange"
                        :current-page="query.currentPage"
                        :page-size="query.pageSize"
                        :total="total">
                    </el-pagination>
                </el-card>
            </div>
        </transition>

        <!-- 新增/编辑对话框 -->
        <el-dialog
            :close-on-click-modal="false"
            :visible.sync="dialogVisible"
            width="700px"
            class="eco-dialog"
            :show-close="false">
            <div slot="title" class="batch-header">
                <div class="batch-header-left">
                    <div class="batch-header-icon"><i class="el-icon-edit-outline"></i></div>
                    <div>
                        <div class="batch-header-title">{{ dialogTitle }}</div>
                        <div class="batch-header-desc">单词信息录入，音频与固定搭配可留空</div>
                    </div>
                </div>
                <i class="el-icon-close batch-header-close" @click="dialogVisible = false"></i>
            </div>
            <el-form :model="formData" ref="formData" :rules="rules" label-width="100px">
                <el-row :gutter="16">
                    <el-col :span="8">
                        <el-form-item label="年级">
                            <el-select v-model="formData.gradeId" disabled style="width: 100%">
                                <el-option v-for="item in gradeList" :key="item.id" :label="item.gradeName" :value="item.id"></el-option>
                            </el-select>
                        </el-form-item>
                    </el-col>
                    <el-col :span="8">
                        <el-form-item label="分册">
                            <el-select v-model="formData.volume" disabled style="width: 100%">
                                <el-option v-for="item in volumeOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
                            </el-select>
                        </el-form-item>
                    </el-col>
                    <el-col :span="8">
                        <el-form-item label="单元">
                            <el-select v-model="formData.unitId" disabled style="width: 100%">
                                <el-option v-for="item in filteredUnitList" :key="item.id" :label="item.unitName" :value="item.id"></el-option>
                            </el-select>
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-row :gutter="16">
                    <el-col :span="12">
                        <el-form-item label="单词" prop="word">
                            <el-input v-model="formData.word" placeholder="请输入单词"></el-input>
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="词性" prop="partOfSpeech">
                            <el-input v-model="formData.partOfSpeech" placeholder="如：n./v./adj."></el-input>
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-form-item label="中文释义" prop="chineseMeaning">
                    <el-input v-model="formData.chineseMeaning" placeholder="请输入中文释义"></el-input>
                </el-form-item>
                <el-form-item label="固定搭配">
                    <el-button size="mini" type="primary" icon="el-icon-plus" @click="addRow('collocationsList')">添加</el-button>
                    <el-table :data="formData.collocationsList" size="small" border stripe style="width:100%;margin-top:8px" max-height="160">
                        <el-table-column label="英文" min-width="160">
                            <template slot-scope="s">
                                <el-input v-model="s.row.english" placeholder="英文" size="mini"/>
                            </template>
                        </el-table-column>
                        <el-table-column label="中文" min-width="160">
                            <template slot-scope="s">
                                <el-input v-model="s.row.chinese" placeholder="中文" size="mini"/>
                            </template>
                        </el-table-column>
                        <el-table-column label="操作" width="60" align="center">
                            <template slot-scope="s">
                                <el-button type="danger" icon="el-icon-delete" size="mini" circle @click="removeRow('collocationsList', s.$index)"/>
                            </template>
                        </el-table-column>
                    </el-table>
                </el-form-item>
                <el-form-item label="派生词">
                    <el-button size="mini" type="primary" icon="el-icon-plus" @click="addRow('wordFormsList')">添加</el-button>
                    <el-table :data="formData.wordFormsList" size="small" border stripe style="width:100%;margin-top:8px" max-height="160">
                        <el-table-column label="英文" min-width="160">
                            <template slot-scope="s">
                                <el-input v-model="s.row.english" placeholder="英文" size="mini"/>
                            </template>
                        </el-table-column>
                        <el-table-column label="中文" min-width="160">
                            <template slot-scope="s">
                                <el-input v-model="s.row.chinese" placeholder="中文" size="mini"/>
                            </template>
                        </el-table-column>
                        <el-table-column label="操作" width="60" align="center">
                            <template slot-scope="s">
                                <el-button type="danger" icon="el-icon-delete" size="mini" circle @click="removeRow('wordFormsList', s.$index)"/>
                            </template>
                        </el-table-column>
                    </el-table>
                </el-form-item>
                <el-row :gutter="16">
                    <el-col :span="12">
                        <el-form-item label="英音音标">
                            <el-input v-model="formData.phoneticUk" placeholder="/.../ "></el-input>
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="美音音标">
                            <el-input v-model="formData.phoneticUs" placeholder="/.../ "></el-input>
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-row :gutter="16">
                    <el-col :span="12">
                        <el-form-item label="英音地址">
                            <el-input v-model="formData.britishEnglishUrl" placeholder="英音音频地址">
                                <el-button slot="append" icon="el-icon-video-play" @click="playAudio(formData.britishEnglishUrl)" :disabled="!formData.britishEnglishUrl"></el-button>
                            </el-input>
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="美音地址">
                            <el-input v-model="formData.americanEnglishUrl" placeholder="美音音频地址">
                                <el-button slot="append" icon="el-icon-video-play" @click="playAudio(formData.americanEnglishUrl)" :disabled="!formData.americanEnglishUrl"></el-button>
                            </el-input>
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-row :gutter="16">
                    <el-col :span="12">
                        <el-form-item label="排序">
                            <el-input-number v-model="formData.sort" :min="0" style="width: 100%"></el-input-number>
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="状态">
                            <el-radio-group v-model="formData.status">
                                <el-radio :label="1">启用</el-radio>
                                <el-radio :label="0">禁用</el-radio>
                            </el-radio-group>
                        </el-form-item>
                    </el-col>
                </el-row>
            </el-form>
            <div slot="footer">
                <el-button @click="dialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitForm" :loading="submitLoading">保存</el-button>
            </div>
        </el-dialog>

        <!-- 导入对话框 -->
        <el-dialog
            :close-on-click-modal="false"
            :visible.sync="importDialogVisible"
            width="640px"
            class="eco-dialog"
            :show-close="false">
            <div slot="title" class="batch-header">
                <div class="batch-header-left">
                    <div class="batch-header-icon"><i class="el-icon-upload2"></i></div>
                    <div>
                        <div class="batch-header-title">导入单词</div>
                        <div class="batch-header-desc">下载模板后填写，单词自动归属到当前单元</div>
                    </div>
                </div>
                <i class="el-icon-close batch-header-close" @click="importDialogVisible = false"></i>
            </div>
            <el-alert
                title="导入说明"
                type="info"
                :closable="false"
                style="margin-bottom: 16px;">
                <template slot="default">
                    <p style="margin:2px 0;"><b>1.</b> 请先下载模板文件，按照模板格式填写数据</p>
                    <p style="margin:2px 0;"><b>2.</b> 支持Excel文件格式（.xlsx, .xls），通过表头列名匹配字段</p>
                    <p style="margin:2px 0;"><b>3.</b> 必填字段：<b>单词</b>、<b>中文释义</b>；选填：英音音标、美音音标、词性、固定搭配、派生词</p>
                    <p style="margin:2px 0;"><b>4.</b> 导入的单词将自动归属到当前单元（年级、分册自动带入）</p>
                    <p style="margin:2px 0;"><b>5.</b> 已存在的单词（按单词去重）会自动更新，否则新增</p>
                    <el-divider style="margin:8px 0;"></el-divider>
                    <p style="margin:2px 0;"><b>6. 固定搭配 / 派生词（多值格式）：</b></p>
                    <p style="margin:2px 0;">&nbsp;&nbsp;· 多个条目用<b>中文分号「；」</b>或<b>英文分号「;」</b>隔开</p>
                    <p style="margin:2px 0;">&nbsp;&nbsp;· 每个条目格式为 <b>英文 + 空格 + 中文</b>，系统以<b>第一个中文字符</b>为界拆分英文与中文</p>
                    <p style="margin:2px 0;">&nbsp;&nbsp;· 若只填英文不填中文，中文部分留空</p>
                    <p style="margin:6px 0 2px 0;">&nbsp;&nbsp;<b>固定搭配示例：</b></p>
                    <p style="margin:2px 0;padding:4px 8px;background:#f5f7fa;border-radius:4px;font-family:monospace;color:#409EFF;">
                        &nbsp;&nbsp;make a decision 做决定；change one's mind 改变主意；in the end 最终
                    </p>
                    <p style="margin:6px 0 2px 0;">&nbsp;&nbsp;<b>派生词示例：</b></p>
                    <p style="margin:2px 0;padding:4px 8px;background:#f5f7fa;border-radius:4px;font-family:monospace;color:#409EFF;">
                        &nbsp;&nbsp;decide v. 决定；decision n. 决定；decisive adj. 果断的
                    </p>
                </template>
            </el-alert>
            <el-button type="primary" icon="el-icon-download" @click="downloadTemplate" style="margin-bottom: 16px;">下载模板</el-button>
            <el-upload
                ref="upload"
                class="upload-demo"
                drag
                action="#"
                :auto-upload="false"
                :on-change="handleFileChange"
                :limit="1"
                accept=".xlsx,.xls"
                :file-list="fileList">
                <i class="el-icon-upload"></i>
                <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
                <div slot="tip" class="el-upload__tip">只能上传xlsx/xls文件</div>
            </el-upload>
            <div slot="footer">
                <el-button @click="importDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="confirmImport" :loading="importLoading">开始导入</el-button>
            </div>
        </el-dialog>

        <!-- 批量导入对话框（按年级+分册，每个 sheet 对应一个单元） -->
        <el-dialog
            :close-on-click-modal="false"
            :visible.sync="batchImportVisible"
            width="640px"
            class="eco-dialog"
            :show-close="false">
            <div slot="title" class="batch-header">
                <div class="batch-header-left">
                    <div class="batch-header-icon"><i class="el-icon-upload2"></i></div>
                    <div>
                        <div class="batch-header-title">批量导入单词</div>
                        <div class="batch-header-desc">{{ batchImportTarget.gradeName }} / {{ batchImportTarget.volume }} — 按 Sheet 匹配单元</div>
                    </div>
                </div>
                <i class="el-icon-close batch-header-close" @click="closeBatchImportDialog"></i>
            </div>

            <el-alert type="warning" :closable="false" style="margin-bottom: 16px;">
                <template slot="default">
                    <p style="margin:2px 0;"><b>1.</b> 下载模板后，每个 <b>Sheet</b> 的名称必须与单元名称一致（如 Unit 1 My School）</p>
                    <p style="margin:2px 0;"><b>2.</b> 每个 Sheet 内的表头和内容格式与单单元导入模板完全相同</p>
                    <p style="margin:2px 0;"><b>3.</b> 系统会根据 Sheet 名称自动匹配对应单元，匹配成功则处理单词数据</p>
                </template>
            </el-alert>

            <el-button type="primary" icon="el-icon-download" @click="downloadTemplate" style="margin-bottom: 16px;">下载模板</el-button>
            <el-upload
                ref="batchUpload"
                class="upload-demo"
                drag
                action="#"
                :auto-upload="false"
                :on-change="handleBatchFileChange"
                :limit="1"
                accept=".xlsx,.xls"
                :file-list="batchFileList">
                <i class="el-icon-upload"></i>
                <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
                <div slot="tip" class="el-upload__tip">只能上传xlsx/xls文件</div>
            </el-upload>

            <div v-if="batchImportResult" class="import-result" style="margin-top: 16px;">
                <el-divider content-position="left">导入结果</el-divider>
                <p>共解析 <b>{{ batchImportResult.total }}</b> 个 Sheet</p>
                <p>新增 <b class="ok">{{ batchImportResult.totalInserted }}</b> 条，更新 <b class="ok">{{ batchImportResult.totalUpdated }}</b> 条</p>
                <div v-if="batchImportResult.skipList && batchImportResult.skipList.length" style="margin-top:8px;">
                    <div v-for="(item, idx) in batchImportResult.skipList" :key="idx" style="color:#e6a23c;font-size:13px;line-height:1.9;">
                        <i class="el-icon-warning"></i> {{ item }}
                    </div>
                </div>
                <div v-if="batchImportResult.sheetResults" style="margin-top:10px;">
                    <el-table :data="Object.values(batchImportResult.sheetResults)" size="mini" border stripe max-height="200">
                        <el-table-column prop="unitName" label="单元" min-width="140"/>
                        <el-table-column prop="inserted" label="新增" width="70" align="center"/>
                        <el-table-column prop="updated" label="更新" width="70" align="center"/>
                        <el-table-column prop="failed" label="失败" width="70" align="center"/>
                    </el-table>
                </div>
            </div>

            <div slot="footer">
                <el-button @click="closeBatchImportDialog">取消</el-button>
                <el-button type="primary" @click="confirmBatchImport" :loading="batchImportLoading" :disabled="!batchUploadFile">开始导入</el-button>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import {reqWordPage, reqWordInsert, reqWordUpdate, reqWordDelete, reqBatchImportWords} from '@/api/education/word'
import {reqUnitPage, reqGradeVolumeStats, reqUnitsByGradeAndVolume, reqVolumeOptions} from '@/api/education/unit'
import {reqGradePage} from '@/api/education/grade'
import request from '@/utils/request'
import global from "@/api/global";

export default {
    name: 'Word',
    data() {
        return {
            list: [],
            total: 0,
            unitList: [],
            gradeList: [],
            gradeVolumeStats: [],
            volumeOptions: [],
            viewLevel: 'gradeVolume',
            currentGradeVolumeItem: null,
            currentUnit: null,
            tableLoading: false,
            navLoading: false,
            submitLoading: false,
            query: {
                currentPage: 1,
                pageSize: 10,
                word: '',
                unitId: null,
                difficultyLevel: null,
                status: null
            },
            dialogVisible: false,
            dialogTitle: '',
            importDialogVisible: false,
            importLoading: false,
            fileList: [],
            uploadFile: null,
            // 批量导入（按 sheet 匹配单元）
            batchImportVisible: false,
            batchImportLoading: false,
            batchUploadFile: null,
            batchFileList: [],
            batchImportTarget: { gradeId: null, gradeName: '', volume: '' },
            batchImportResult: null,
            formData: {
                id: null,
                word: '',
                chineseMeaning: '',
                phoneticUk: '',
                phoneticUs: '',
                partOfSpeech: '',
                collocations: '',
                wordForms: '',
                collocationsList: [],
                wordFormsList: [],
                gradeId: null,
                volume: '',
                unitId: null,
                exampleSentence: '',
                britishEnglishUrl: '',
                americanEnglishUrl: '',
                sort: 0,
                status: 1
            },
            rules: {
                word: [
                    { required: true, message: '请输入单词', trigger: 'blur' }
                ],
                partOfSpeech: [
                    { required: true, message: '请输入词性', trigger: 'blur' }
                ],
                chineseMeaning: [
                    { required: true, message: '请输入中文释义', trigger: 'blur' }
                ]
            }
        }
    },
    computed: {
        currentGradeVolumeText() {
            if (!this.currentGradeVolumeItem) return ''
            return `${this.currentGradeVolumeItem.gradeName}${this.currentGradeVolumeItem.volume}`
        },
        currentUnitLabel() {
            if (!this.currentGradeVolumeItem || !this.currentUnit) return ''
            return `${this.currentGradeVolumeItem.gradeName}${this.currentGradeVolumeItem.volume} / ${this.currentUnit.unitName}`
        },
        filteredUnitsByGradeVolume() {
            if (!this.currentGradeVolumeItem) {
                return []
            }
            return this.unitList.filter(unit =>
                unit.gradeId === this.currentGradeVolumeItem.gradeId &&
                unit.volume === this.currentGradeVolumeItem.volume
            ).sort((a, b) => a.unitNumber - b.unitNumber)
        },
        filteredUnitList() {
            if (!this.formData.gradeId || !this.formData.volume) {
                return []
            }
            return this.unitList.filter(unit =>
                unit.gradeId === this.formData.gradeId &&
                unit.volume === this.formData.volume
            )
        }
    },
    async created() {
        await this.loadGradeList()
        await this.loadUnitList()
        await this.loadGradeVolumeStats()
        await this.restoreStateFromUrl()
    },
    methods: {
        async loadGradeList() {
            try {
                const data = await reqGradePage({currentPage: 1, pageSize: 100})
                this.gradeList = data.records || []
            } catch (e) {
                console.error('加载年级列表失败:', e)
            }
        },
        async loadUnitList() {
            try {
                const data = await reqUnitPage({currentPage: 1, pageSize: 1000})
                this.unitList = data.records || []
            } catch (e) {
                console.error('加载单元列表失败:', e)
            }
        },
        async loadGradeVolumeStats() {
            try {
                const data = await reqGradeVolumeStats()
                this.gradeVolumeStats = data || []
            } catch (e) {
                console.error('加载年级分册统计失败:', e)
            }
        },
        async selectGradeVolume(gradeId, gradeName, volume) {
            this.navLoading = true
            this.currentGradeVolumeItem = { gradeId, gradeName, volume }
            try {
                const units = await reqUnitsByGradeAndVolume(gradeId, volume)
                this.unitList = units || []
                this.viewLevel = 'unit'
                this.updateUrlParams()
            } catch (e) {
                this.$message.error('加载单元列表失败')
            } finally {
                this.navLoading = false
            }
        },
        selectUnit(unit) {
            this.currentUnit = unit
            this.query.unitId = unit.id
            this.viewLevel = 'word'
            this.load()
            this.updateUrlParams()
        },
        goToGradeVolume() {
            this.viewLevel = 'gradeVolume'
            this.currentGradeVolumeItem = null
            this.currentUnit = null
            this.query.unitId = null
            this.list = []
            this.total = 0
            this.loadUnitList()
            this.clearUrlParams()
        },
        goToUnitList() {
            this.viewLevel = 'unit'
            this.currentUnit = null
            this.query.unitId = null
            this.list = []
            this.total = 0
            this.updateUrlParams()
        },
        async load() {
            if (this.viewLevel !== 'word' || !this.currentUnit) {
                return
            }
            this.tableLoading = true
            try {
                const data = await reqWordPage(this.query)
                this.list = data.records || []
                this.total = data.total || 0
            } catch (e) {
                this.$message.error('加载单词列表失败')
                console.error(e)
            } finally {
                this.tableLoading = false
            }
        },
        handleSearch() {
            this.query.currentPage = 1
            this.load()
        },
        handleReset() {
            this.query = {
                currentPage: 1,
                pageSize: 10,
                word: '',
                unitId: this.currentUnit ? this.currentUnit.id : null,
                difficultyLevel: null,
                status: null
            }
            this.load()
        },
        currentChange(num) {
            this.query.currentPage = num
            this.load()
        },
        sizeChange(size) {
            this.query.pageSize = size
            this.query.currentPage = 1
            this.load()
        },
        btnInsert(row) {
            if (row) {
                this.dialogTitle = '编辑单词'
                this.formData = JSON.parse(JSON.stringify(row))
                this.formData.collocationsList = this.parseJsonArr(this.formData.collocations)
                this.formData.wordFormsList = this.parseJsonArr(this.formData.wordForms)
                const unit = this.unitList.find(u => u.id === row.unitId)
                if (unit) {
                    this.formData.gradeId = unit.gradeId
                    this.formData.volume = unit.volume
                }
            } else {
                this.dialogTitle = '新增单词'
                this.formData = {
                    id: null,
                    word: '',
                    chineseMeaning: '',
                    phoneticUk: '',
                    phoneticUs: '',
                    partOfSpeech: '',
                    collocations: '',
                    wordForms: '',
                    collocationsList: [],
                    wordFormsList: [],
                    gradeId: this.currentGradeVolumeItem ? this.currentGradeVolumeItem.gradeId : null,
                    volume: this.currentGradeVolumeItem ? this.currentGradeVolumeItem.volume : '',
                    unitId: this.currentUnit ? this.currentUnit.id : null,
                    difficultyLevel: 1,
                    frequency: 0,
                    exampleSentence: '',
                    audioUrl: '',
                    imageUrl: '',
                    tags: '',
                    sort: 0,
                    status: 1
                }
            }
            // 加载该年级对应的分册选项（用于disabled下拉框展示）
            if (this.formData.gradeId) {
                this.loadFormVolumeOptions(this.formData.gradeId)
            }
            this.$nextTick(() => {
                if (this.$refs.formData) {
                    this.$refs.formData.clearValidate()
                }
            })
            this.dialogVisible = true
        },
        async loadFormVolumeOptions(gradeId) {
            try {
                const res = await reqVolumeOptions(gradeId)
                const list = (res && Array.isArray(res)) ? res : []
                this.volumeOptions = list.map(v => ({ label: v, value: v }))
            } catch (e) {
                console.error('加载分册选项失败:', e)
            }
        },
        async btnDelete(id) {
            try {
                await this.$confirm('此操作将永久删除该单词，是否继续？', '删除确认', {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    type: 'warning'
                })
                await reqWordDelete(id)
                this.$message.success('删除成功')
                this.load()
            } catch (e) {
                if (e !== 'cancel') {
                    this.$message.error(e.message || '删除失败')
                }
            }
        },
        submitForm() {
            this.$refs.formData.validate(async valid => {
                if (!valid) return

                this.submitLoading = true
                try {
                    const submitData = {
                        ...this.formData,
                        collocations: JSON.stringify(this.formData.collocationsList),
                        wordForms: JSON.stringify(this.formData.wordFormsList),
                        collocationsList: undefined,
                        wordFormsList: undefined,
                        gradeId: undefined,
                        volume: undefined
                    }
                    await (submitData.id
                        ? reqWordUpdate(submitData)
                        : reqWordInsert(submitData))
                    this.$message.success('操作成功')
                    this.dialogVisible = false
                    this.load()
                } catch (e) {
                    this.$message.error(e.message || '操作失败')
                } finally {
                    this.submitLoading = false
                }
            })
        },
        showImportDialog() {
            this.importDialogVisible = true
            this.fileList = []
            this.uploadFile = null
        },
        handleFileChange(file, fileList) {
            this.uploadFile = file.raw
            this.fileList = fileList
        },
        downloadTemplate() {
            let fileUrl = global.preview_path_url + "单元导入单词模板.xlsx"
            window.open(fileUrl, '_blank')
        },
        async confirmImport() {
            if (!this.uploadFile) {
                this.$message.warning('请先选择要导入的文件')
                return
            }
            if (!this.currentUnit) {
                this.$message.warning('请先选择一个单元')
                return
            }
            this.importLoading = true
            try {
                const formData = new FormData()
                formData.append('file', this.uploadFile)
                formData.append('unitId', this.currentUnit.id)

                const result = await request({
                    url: '/education/word/import',
                    method: 'post',
                    data: formData,
                    headers: { 'Content-Type': 'multipart/form-data' }
                })

                let message = '导入完成！'
                if (result.updateCount > 0) {
                    message += `新增：${result.successCount - result.updateCount}条，更新：${result.updateCount}条`
                } else {
                    message += `成功：${result.successCount}条`
                }
                if (result.failCount > 0) {
                    message += `，失败：${result.failCount}条`
                }
                this.$message.success(message)

                if (result.errors && result.errors.length > 0) {
                    console.warn('导入错误详情：', result.errors)
                    this.$alert(result.errors.join('<br/>'), '导入错误详情', {
                        dangerouslyUseHTMLString: true,
                        confirmButtonText: '确定'
                    })
                }
                this.importDialogVisible = false
                this.load()
            } catch (e) {
                this.$message.error('导入失败：' + (e.message || '未知错误'))
            } finally {
                this.importLoading = false
            }
        },
        parseJsonArr(str) {
            if (!str) return []
            if (Array.isArray(str)) return str
            try { const v = JSON.parse(str); return Array.isArray(v) ? v : [] } catch { return [{ english: str, chinese: '' }] }
        },
        formatJsonArr(str) {
            const arr = this.parseJsonArr(str)
            return arr.map(i => i.english || '').join('； ')
        },
        addRow(listKey) {
            this.formData[listKey].push({ english: '', chinese: '' })
        },
        removeRow(listKey, index) {
            this.formData[listKey].splice(index, 1)
        },
        updateUrlParams() {
            const query = { ...this.$route.query }
            if (this.currentGradeVolumeItem) {
                query.gradeId = this.currentGradeVolumeItem.gradeId
                query.gradeName = this.currentGradeVolumeItem.gradeName
                query.volume = this.currentGradeVolumeItem.volume
            } else {
                delete query.gradeId
                delete query.gradeName
                delete query.volume
            }
            if (this.currentUnit) {
                query.unitId = this.currentUnit.id
            } else {
                delete query.unitId
            }
            this.$router.replace({ query })
        },
        clearUrlParams() {
            this.$router.replace({ query: {} })
        },
        async restoreStateFromUrl() {
            const query = this.$route.query
            if (query.gradeId && query.volume) {
                this.currentGradeVolumeItem = {
                    gradeId: Number(query.gradeId),
                    gradeName: query.gradeName || '',
                    volume: query.volume
                }
                try {
                    const units = await reqUnitsByGradeAndVolume(Number(query.gradeId), query.volume)
                    this.unitList = units || []
                    this.viewLevel = 'unit'
                    if (query.unitId) {
                        const unitId = Number(query.unitId)
                        const unit = this.unitList.find(u => u.id === unitId)
                        if (unit) {
                            this.selectUnit(unit)
                        }
                    }
                } catch (e) {
                    console.error('恢复导航状态失败:', e)
                }
            }
        },
        playAudio(url) {
            if (!url) return
            const audio = new Audio(url)
            audio.play().catch(e => {
                this.$message.error('音频播放失败：' + (e.message || '无法播放'))
            })
        },
        // ===== 批量导入（按 sheet 匹配单元）=====
        openBatchImportDialog(gradeId, gradeName, volume) {
            this.batchImportTarget = { gradeId, gradeName, volume }
            this.batchImportVisible = true
            this.batchUploadFile = null
            this.batchFileList = []
            this.batchImportResult = null
            if (this.$refs.batchUpload) {
                this.$refs.batchUpload.clearFiles()
            }
        },
        closeBatchImportDialog() {
            this.batchImportVisible = false
            this.batchUploadFile = null
            this.batchImportResult = null
        },
        handleBatchFileChange(file, fileList) {
            this.batchUploadFile = file.raw
            this.batchFileList = fileList
            this.batchImportResult = null
        },
        async confirmBatchImport() {
            if (!this.batchUploadFile) {
                this.$message.warning('请先选择 Excel 文件')
                return
            }
            const formData = new FormData()
            formData.append('file', this.batchUploadFile)
            formData.append('gradeId', this.batchImportTarget.gradeId)
            formData.append('volume', this.batchImportTarget.volume)

            this.batchImportLoading = true
            try {
                const res = await reqBatchImportWords(formData)
                this.batchImportResult = res

                let msg = '批量导入完成！'
                if (res.totalInserted > 0 || res.totalUpdated > 0) {
                    msg += ` 新增${res.totalInserted}条，更新${res.totalUpdated}条`
                }
                if (res.skipList && res.skipList.length > 0) {
                    msg += `，跳过${res.skipList.length}个Sheet`
                }
                this.$message.success(msg)
            } catch (e) {
                this.$message.error(e.message || '批量导入失败')
            } finally {
                this.batchImportLoading = false
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.word-page {
    .filter-card {
        margin: 10px 15px;

        ::v-deep .el-card__header {
            padding: 16px 20px;
            background: #fafafa;
            border-bottom: 1px solid #ebeef5;
        }
    }

    .table-card {
        margin: 10px 15px;

        ::v-deep .el-card__header {
            padding: 14px 20px;
            background: #fafafa;
            border-bottom: 1px solid #ebeef5;
        }
    }

    .card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;

        .card-header-left {
            display: flex;
            align-items: center;
            font-size: 15px;
            font-weight: 600;
            color: #303133;

            i {
                font-size: 18px;
                margin-right: 6px;
                color: #409EFF;
            }

            .el-divider {
                margin: 0 10px;
                background-color: #dcdfe6;
            }

            .header-sub {
                font-weight: 400;
                font-size: 13px;
                color: #909399;
            }

            .back-btn {
                font-size: 13px;
                padding: 0;
                color: #409EFF;
                font-weight: 400;
            }

            .total-badge {
                margin-left: 16px;
                font-weight: 400;
                font-size: 13px;
                color: #606266;

                b {
                    color: #409EFF;
                }
            }
        }
    }

    .filter-form {
        ::v-deep .el-form-item {
            margin-bottom: 0;
        }
    }

    .action-btns {
        display: flex;
        justify-content: center;
        align-items: center;
        white-space: nowrap;
    }

    ::v-deep .el-table {
        th.el-table__cell {
            background-color: #fafafa;
            color: #606266;
            font-weight: 600;
        }
    }

    // 年级分组
    .grade-group {
        margin-bottom: 24px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    .grade-title {
        font-size: 16px;
        font-weight: 600;
        color: #303133;
        margin-bottom: 12px;
        padding-left: 10px;
        border-left: 3px solid #409EFF;
    }

    // 导航卡片通用
    .nav-card {
        margin-bottom: 16px;
        cursor: pointer;
        transition: all 0.3s;
        position: relative;

        &:hover {
            transform: translateY(-4px);
            box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);

            .card-arrow {
                opacity: 1;
                transform: translateX(0);
            }
        }

        ::v-deep .el-card__body {
            padding: 20px 16px;
        }

        .card-title {
            font-size: 17px;
            font-weight: 600;
            color: #303133;
            margin-bottom: 8px;
            text-align: center;
        }

        .card-info {
            font-size: 13px;
            color: #909399;
            text-align: center;
        }

        .card-arrow {
            position: absolute;
            right: 12px;
            top: 50%;
            transform: translateY(-50%) translateX(-4px);
            opacity: 0;
            transition: all 0.3s;
            color: #409EFF;
            font-size: 18px;
        }

        .card-batch-import {
            position: absolute;
            top: 8px;
            right: 8px;
            display: flex;
            align-items: center;
            gap: 4px;
            padding: 4px 10px;
            font-size: 12px;
            color: #2e8b74;
            background: rgba(46, 139, 116, 0.08);
            border: 1px solid rgba(46, 139, 116, 0.25);
            border-radius: 12px;
            cursor: pointer;
            transition: all 0.25s;
            z-index: 1;

            i { font-size: 13px; }

            &:hover {
                background: #2e8b74;
                color: #fff;
                border-color: #2e8b74;
                box-shadow: 0 2px 8px rgba(46, 139, 116, 0.3);
            }
        }

        &.grade-volume-card:hover .card-batch-import {
            opacity: 1;
        }
    }
}

// 视图切换过渡
.view-fade-enter-active,
.view-fade-leave-active {
    transition: opacity 0.2s ease;
}
.view-fade-enter,
.view-fade-leave-to {
    opacity: 0;
}

// 内联加载
.inline-loading {
    text-align: center;
    padding: 40px 0;
    color: #909399;
    font-size: 14px;
    i {
        font-size: 20px;
        margin-right: 6px;
        animation: rotating 1s linear infinite;
    }
}
@keyframes rotating {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

@media (max-width: 768px) {
    .word-page {
        .filter-card,
        .table-card {
            margin: 8px 6px;
        }
    }
}
</style>
